
import React, { useState } from "react";
import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { 
  Select,
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";

interface CreateCollaborationFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export const CreateCollaborationForm = ({ onSubmit, onCancel }: CreateCollaborationFormProps) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    location: "Remote",
    timeline: "",
    budget: "",
    teamSize: "3",
    skillsNeeded: [] as string[]
  });
  
  const [skillInput, setSkillInput] = useState("");
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSkill = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && skillInput.trim()) {
      e.preventDefault();
      if (!formData.skillsNeeded.includes(skillInput.trim())) {
        setFormData(prev => ({
          ...prev,
          skillsNeeded: [...prev.skillsNeeded, skillInput.trim()]
        }));
      }
      setSkillInput("");
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      skillsNeeded: prev.skillsNeeded.filter(s => s !== skill)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.category) {
      return; // Form validation would go here
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="title">Project Title <span className="text-red-500">*</span></Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="e.g., E-commerce Website Redesign"
            required
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="description">Project Description <span className="text-red-500">*</span></Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe your project and what you're looking to achieve..."
            required
            className="mt-1 min-h-[120px]"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="category">Category <span className="text-red-500">*</span></Label>
            <Select 
              value={formData.category}
              onValueChange={(value) => handleSelectChange("category", value)}
              required
            >
              <SelectTrigger id="category" className="mt-1">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="web">Web Development</SelectItem>
                <SelectItem value="mobile">Mobile Apps</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="content">Content Creation</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="location">Location</Label>
            <Select 
              value={formData.location}
              onValueChange={(value) => handleSelectChange("location", value)}
            >
              <SelectTrigger id="location" className="mt-1">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Remote">Remote</SelectItem>
                <SelectItem value="Hybrid">Hybrid</SelectItem>
                <SelectItem value="On-site">On-site</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="timeline">Timeline</Label>
            <Select 
              value={formData.timeline}
              onValueChange={(value) => handleSelectChange("timeline", value)}
            >
              <SelectTrigger id="timeline" className="mt-1">
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-2 weeks">1-2 weeks</SelectItem>
                <SelectItem value="2-4 weeks">2-4 weeks</SelectItem>
                <SelectItem value="1-3 months">1-3 months</SelectItem>
                <SelectItem value="3-6 months">3-6 months</SelectItem>
                <SelectItem value="6+ months">6+ months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="budget">Budget Range</Label>
            <Select 
              value={formData.budget}
              onValueChange={(value) => handleSelectChange("budget", value)}
            >
              <SelectTrigger id="budget" className="mt-1">
                <SelectValue placeholder="Select budget" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Under $1,000">Under $1,000</SelectItem>
                <SelectItem value="$1,000 - $5,000">$1,000 - $5,000</SelectItem>
                <SelectItem value="$5,000 - $10,000">$5,000 - $10,000</SelectItem>
                <SelectItem value="$10,000 - $20,000">$10,000 - $20,000</SelectItem>
                <SelectItem value="$20,000+">$20,000+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div>
          <Label htmlFor="teamSize">Team Size</Label>
          <Select 
            value={formData.teamSize}
            onValueChange={(value) => handleSelectChange("teamSize", value)}
          >
            <SelectTrigger id="teamSize" className="mt-1">
              <SelectValue placeholder="Select team size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2 members</SelectItem>
              <SelectItem value="3">3 members</SelectItem>
              <SelectItem value="4">4 members</SelectItem>
              <SelectItem value="5">5 members</SelectItem>
              <SelectItem value="6+">6+ members</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="skills">Skills Needed</Label>
          <Input
            id="skills"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={handleAddSkill}
            placeholder="Type skill and press Enter to add"
            className="mt-1"
          />
          
          {formData.skillsNeeded.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {formData.skillsNeeded.map((skill, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {skill}
                  <button
                    type="button"
                    onClick={() => handleRemoveSkill(skill)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <DialogFooter className="flex space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Create Project
        </Button>
      </DialogFooter>
    </form>
  );
};
